import { Plus } from 'lucide-react';
import Color from 'color';
import get from 'lodash/get.js';

// Helper function to determine text color based on background color
const getTextColor = backgroundColor => {
  try {
    return Color(backgroundColor).isDark() ? '#ffffff' : '#000000';
  } catch {
    return '#000000';
  }
};

// Helper function to create a colored dot icon for status
const createStatusDotIcon = status => {
  const textColor = getTextColor(status.colour);

  return (
    <div
      className="size-4 rounded-full border flex-shrink-0"
      style={{
        backgroundColor: status.colour,
        borderColor:
          textColor === '#ffffff' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'
      }}
    />
  );
};

/**
 * Custom hook that builds assign with status actions for resource status management
 *
 * @param {Object} params - Parameters for the hook
 * @param {Object} params.booking - Current booking object
 * @param {Object} params.resource - Resource object
 * @param {Object} params.slot - Slot object
 * @param {Object} params.production - Production object
 * @param {Array} params.statusesList - List of available statuses
 * @param {Function} params.onStatusUpdate - Required callback function to handle status updates
 * @returns {Array} Array of actions for ButtonWithDropdown
 *
 * @example
 * const assignWithStatusActions = buildAssignWithStatus({
 *   booking,
 *   resource,
 *   slot,
 *   production,
 *   statusesList: bookingStatuses,
 *   onStatusUpdate: ({ status, booking, resource, slot, production }) => {
 *     // Custom logic for handling status updates
 *     console.log('Status update:', { status });
 *     // Your custom implementation here
 *   }
 * });
 */
export const buildAssignWithStatus = ({
  booking,
  resource,
  slot,
  production,
  statusesList = [],
  onStatusUpdate
}) => {
  // Returns a nested menu of assign actions for each available status

  if (!Array.isArray(statusesList) || statusesList.length === 0) return [];

  // Check if resource is already assigned to this production
  const resourceId = resource?.id;
  const resourceSlotId = slot?.id;
  const existingAssignments = get(booking, 'resource_slot_assignments', []);
  const isAlreadyAssigned = existingAssignments.some(
    assignment =>
      assignment.contact_id === resourceId &&
      assignment.resource_slot_id === resourceSlotId
  );

  return [
    {
      text: isAlreadyAssigned ? 'Change status' : 'Assign with status',
      icon: <Plus className="w-4 h-4" />,
      search: {
        searchFunction: query => {
          const statuses = statusesList.filter(status =>
            status.name.toLowerCase().includes(query.toLowerCase())
          );
          return statuses.map(status => ({
            text: (
              <div className="flex items-center gap-2 w-full justify-between">
                {status.name || status.text || status.status_type}
              </div>
            ),
            icon: createStatusDotIcon(status),
            statusColor: status.colour, // Add status color for potential styling
            action: () => {
              onStatusUpdate({
                status,
                booking,
                resource,
                slot,
                production
              });
            }
          }));
        }
      }
    }
  ];
};
